import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  FileObject,
  FormControlModel,
  PROMPTS,
  RelationModel,
  StepperGroupModel,
  StepperModel,
  ValueListsService,
  anyAsset,
} from '@thehubz/client/core';
import {
  Asset,
  AssetDomain,
  AssetsApiService,
  AssetsBulkEditApiService,
  CompanyBrand,
  CreateAssetRelationDto,
  RelationsApiService,
  Tags,
  UpdateAssetManyDto,
  UpdateProductionDetailsDto,
  UserAgencyModel,
  UserInfo,
} from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, MULTI_VALUE } from '@thehubz/shared/model';
import { iif, map, of, switchMap } from 'rxjs';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetEditStepperComponent } from '../asset-edit-stepper/asset-edit-stepper.component';
import { AssetItemBasicComponent } from '../asset-item-basic/asset-item-basic.component';
import { handleStandardBranding, handleVorwerkBranding } from '../asset-upload/asset-upload.util';
import { AssetSettingsStepperConfigService } from '../asset-upload/stepper-config/asset-settings-stepper-config.service';
import { arraysEqual, findCommonValues, getStatusOrAwaitingApproval, handleGeneralInformation } from './asset-edit-popup.util';
import { AssetEditStepperConfigService } from './stepper-config/asset-edit-stepper-config.service';

export interface AssetListFileObject extends FileObject {
  previewUrl: string;
  assetType: Asset.AssetTypeEnum;
  fallbackImg?: string;
  imageUrl?: string;
}

@Component({
  selector: 'thehubz-asset-edit-popup',
  templateUrl: './asset-edit-popup.component.html',
  styleUrls: ['./asset-edit-popup.component.scss'],
  standalone: true,
  imports: [CommonModule, ClientCoreModule, AssetEditStepperComponent, AssetItemBasicComponent],
})
export class AssetEditPopupComponent implements OnInit {
  files: FileObject[] = [];
  assetListFiles: AssetListFileObject[] = [];
  existingAssetListFiles: AssetUIModel[] = [];
  relationData!: RelationModel;
  storedRelationData!: RelationModel;
  selectedAssetIds: string[] = [];
  selectedAssetRelations: AnyType = [];
  agencies?: UserAgencyModel[];
  clients: AnyType[] = [];
  brands: CompanyBrand[] = [];
  assets: Asset[] = [];
  assetsMap = new Map<string, Asset>(); // used for asset relations
  attachments: AnyType[] = [];
  progress = 0;
  fileLength = 0;
  stepperType = '';
  currentUploadFileName = '';
  tags: string[] = [];
  expiryRequired = true;
  errorFlag = true;
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text
  AnyAsset = anyAsset;
  users!: { id: string; name: string; email: string }[];
  stepperData!: {
    stepperData: StepperModel;
    additionalData: AnyType[];
    callFrom: string;
    editable: boolean;
  };
  applicationData: AnyType = {};
  currentStep!: StepperGroupModel;
  hasFilesError = false;
  domainsMatchingFlag = true;
  isReady = false;
  brandingKey!: CustomClients;
  /** Store all tags associated with asset */
  tagsInfo: Tags[] = [];
  cumulatedTags: Tags[] = [];

  checkAssetsStatus!: boolean;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    @Inject(MAT_DIALOG_DATA) private readonly data: AnyType,
    private readonly canPipe: CanPipe,
    private readonly assetsService: AssetsApiService,
    private readonly commonUtilityLib: CommonUtilityLib,
    private readonly valueListsService: ValueListsService,
    private readonly relationService: RelationsApiService,
    private readonly assetCommonService: AssetCommonService,
    private readonly assetUrlService: AssetsGenerateUrlService,
    private readonly bulkEditApiService: AssetsBulkEditApiService,
    private readonly dialogRef: MatDialogRef<AssetEditPopupComponent>,
    private readonly assetEditStepperConfigService: AssetEditStepperConfigService,
    private readonly clientNotificationHandlerService: ClientNotificationHandlerService,
    private readonly assetSettingsStepperConfigService: AssetSettingsStepperConfigService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    if (this.data && this.data['editType']) {
      this.stepperType = this.data['editType'];
    }
    this.domainsMatchingFlag = this.data?.domainMatching ?? this.domainsMatchingFlag;
    this.populateUsers().then(async () => {
      let stepperData: StepperModel;
      const tagsAccessibility = !this.canPipe.transform(this.AnyAsset, 'tagging');

      switch (this.brandingKey) {
        case CustomClients.LufthansaMediacenter:
        case CustomClients.LufthansaMedialounge:
          stepperData = await this.assetEditStepperConfigService.getLufthansaAssetSettingsStepperConfig(
            this.users,
            { disableTag: tagsAccessibility },
            this.stepperType,
          );
          break;
        case CustomClients.SEAT:
          stepperData = await this.assetEditStepperConfigService.getSeatAssetSettingsStepperConfig(
            this.users,
            { disableTag: tagsAccessibility },
            this.stepperType,
          );
          break;
        case CustomClients.VKB:
          stepperData = await this.assetEditStepperConfigService.getVKBAssetSettingsStepperConfig(
            this.users,
            { disableTag: tagsAccessibility },
            this.stepperType,
          );
          break;
        case CustomClients.Arval:
          stepperData = await this.assetEditStepperConfigService.getArvalAssetSettingsStepperConfig(
            this.users,
            { disableTag: tagsAccessibility },
            this.stepperType,
          );
          break;
        case CustomClients.Vorwerk:
          stepperData = await this.assetEditStepperConfigService.getVorwerkAssetSettingsStepperConfig(
            this.users,
            { disableTag: tagsAccessibility },
            this.stepperType,
          );
          break;
        default:
          stepperData = await this.assetEditStepperConfigService.getAssetSettingsStepperConfig(this.users, { disableTag: tagsAccessibility }, this.stepperType);
          break;
      }
      const stepperMasterData = JSON.parse(JSON.stringify(stepperData));
      if (this.data && this.data['assets'] && this.data['assets'].length > 0) {
        this.checkAssetsStatus = this.data['assets'].every((asset: AnyType) => asset.status === Asset.StatusEnum.Approved);
        this.data['assets'].forEach((asset: AnyType) => {
          this.existingAssetListFiles.push(asset);
          this.assetListFiles.push(asset);
          this.selectedAssetIds.push(asset._id);
        });
      }
      this.currentStep = stepperMasterData.stepper[0];
      if (this.stepperType === 'metaData') {
        const formFields = stepperMasterData.stepper[0]?.formFields;
        this.assetSettingsStepperConfigService.updateStatus(this.brandingKey, formFields);
        const statusField = formFields?.find((field: AnyType) => field.formFieldName === 'status');
        const idx = statusField.options.findIndex((options: AnyType) => options._id == MULTI_VALUE);
        if (this.existingAssetListFiles.length > 1) {
          await this.getTagSeparatedList();
          if (idx == -1) {
            statusField.options?.push({ id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
          }
        } else {
          if (this.existingAssetListFiles && this.existingAssetListFiles[0]) {
            let assetTags: AnyType = [];
            assetTags = (this.existingAssetListFiles[0] && this.existingAssetListFiles[0]?.tags) || [];
            await this.addTags(assetTags);
          }
        }
        this.valueListsService.listUserAgencies(['asset.upload']).subscribe((response) => {
          this.agencies = response;
          const fields = stepperMasterData.stepper[0].formFields;
          let agencyField = fields?.find((field: AnyType) => {
            return field.formFieldName === 'agency';
          });
          if (this.brandingKey === CustomClients.SEAT || this.brandingKey === CustomClients.Vorwerk) {
            const domainField = fields?.find((field: AnyType) => {
              return field.formFieldName === AssetMetadata.General.DOMAINS;
            });
            if (domainField) {
              agencyField = domainField.formFieldGroup?.find((field: AnyType) => {
                return field.formFieldName === 'agency';
              });
            }
          }
          if (agencyField && this.existingAssetListFiles.length > 1) {
            const idx = response.findIndex((options: AnyType) => options._id == MULTI_VALUE);
            const domainField = fields?.find((field: AnyType) => {
              return field.formFieldName === AssetMetadata.General.DOMAINS;
            });
            if (idx == -1 && !domainField) {
              response.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE, clients: [] });
            }
          }
          if (agencyField) agencyField.options = response;
          this.updateApplicationData(stepperMasterData);
        });
      } else if (this.stepperType === AssetMetadata.Rights.USAGE_RIGHTS) {
        this.updateApplicationData(stepperMasterData);
      } else {
        const assetListData: AnyType = { list: this.selectedAssetIds };
        this.relationService.relationsControllerFindRelations(JSON.stringify(assetListData)).subscribe((response: AnyType) => {
          this.relationData = response;
          this.storedRelationData = JSON.parse(JSON.stringify(this.relationData));
          const tempAssetDetailsObject: AnyType = [];
          this.storedRelationData.assets.forEach((element: AnyType) => {
            const idx = this.storedRelationData.assetDetails.findIndex((assetDetails: AssetUIModel) => assetDetails._id == element.assetId);
            if (idx != -1) {
              tempAssetDetailsObject.push(this.storedRelationData.assetDetails[idx]);
            }
          });
          this.selectedAssetRelations = this.assetUrlService.generatePreview(tempAssetDetailsObject);
          this.updateApplicationData(stepperMasterData);
        });
      }
    });
  }

  onStepChange(currentStep: StepperGroupModel): void {
    this.currentStep = currentStep;
  }

  async getTagSeparatedList() {
    let uniqueArray: string[] = [];
    let intersectionArray: string[] = [];
    let noIntersectionFlag = false;
    this.existingAssetListFiles.forEach((asset: AnyType) => {
      const tagsArray = asset.tags || [];
      if (intersectionArray.length === 0 && !noIntersectionFlag) {
        intersectionArray = tagsArray;
      } else {
        intersectionArray = tagsArray.filter((value: string) => intersectionArray.includes(value));
        noIntersectionFlag = intersectionArray.length === 0 ? true : false;
      }
      uniqueArray = uniqueArray.concat(tagsArray);
    });
    // Getting the unique Array List
    uniqueArray = uniqueArray.filter((item, index) => uniqueArray.indexOf(item) === index);
    // Removing the intersection array from unique array
    uniqueArray = uniqueArray.filter(function (obj) {
      return intersectionArray.indexOf(obj) == -1;
    });
    if (uniqueArray.length > 0) {
      this.cumulatedTags = await this.valueListsService.listTags(uniqueArray);
    }
    if (intersectionArray.length > 0) {
      await this.addTags(intersectionArray);
    }
  }

  updateTagObject(evt: AnyType) {
    this.cumulatedTags = evt;
  }

  async addTags(assetTags: string[]) {
    if (assetTags.length) {
      const tagIds: string[] = assetTags;
      this.tagsInfo = await this.valueListsService.listTags(tagIds);
      // Map the sorted array to get tag names
      this.tagsInfo.forEach((tag) => {
        if (tag.name) {
          this.tags.push(tag.name);
        }
      });
    }
  }

  updateApplicationData(stepperData: StepperModel) {
    this.stepperData = {
      stepperData: stepperData,
      additionalData: [],
      callFrom: 'assetUpload',
      editable: true,
    };
    this.applicationData = this.existingAssetListFiles.length > 0 ? this.getApplicationData(this.existingAssetListFiles) : null;
    this.isReady = true;
  }

  getApplicationData(selectedAssets: AssetUIModel[]) {
    const currentStepperData = this.stepperData.stepperData;
    const finalAssetObject: AssetUIModel = JSON.parse(JSON.stringify(selectedAssets[0]));

    if (!currentStepperData.stepper?.length) return finalAssetObject;

    for (const stepper of currentStepperData.stepper) {
      if (stepper.stepperGroupName === 'productionDetails') {
        this.processProductionField(stepper.formFields || [], finalAssetObject);
        continue;
      }

      for (const formField of stepper.formFields || []) {
        const formFieldName = formField.formFieldName;

        if (formFieldName === AssetMetadata.General.TAGS) {
          this.processTagsField(formField, finalAssetObject);
        } else if (formFieldName === AssetMetadata.General.DOMAINS) {
          this.processDomainsField(finalAssetObject);
        } else if (finalAssetObject && (finalAssetObject[formFieldName] || finalAssetObject[formFieldName] === null)) {
          this.processStandardField(formField, formFieldName, selectedAssets, finalAssetObject);
        } else if (finalAssetObject?.['customFields']) {
          this.processCustomField(formField, formFieldName, selectedAssets, finalAssetObject);
        }
      }
    }

    return finalAssetObject;
  }

  private processTagsField(formField: FormControlModel, finalAssetObject: AssetUIModel) {
    // In bulk edit we do not need minimum tag validation.
    if (formField.validators) {
      formField.validators.minLength = 0;
    }
    finalAssetObject[AssetMetadata.General.TAGS] = this.tags;
  }

  private processDomainsField(finalAssetObject: AssetUIModel) {
    if (!this.domainsMatchingFlag) {
      finalAssetObject[AssetMetadata.General.DOMAINS] = [finalAssetObject[AssetMetadata.General.DOMAINS]?.[0] as AssetDomain];
    }
  }

  private processStandardField(formField: FormControlModel, formFieldName: string, selectedAssets: AssetUIModel[], finalAssetObject: AssetUIModel) {
    const currentFieldValue = finalAssetObject[formFieldName];

    const isMismatched = selectedAssets.some((assetObject) => {
      if (currentFieldValue?._id) {
        return assetObject[formFieldName]?._id !== currentFieldValue._id;
      } else if (formField.fieldType === AssetMetadata.General.DOMAINS) {
        return (
          assetObject[formFieldName]?.agency !== currentFieldValue.agency ||
          assetObject[formFieldName]?.client !== currentFieldValue.client ||
          assetObject[formFieldName]?.brand !== currentFieldValue.brand
        );
      } else {
        return assetObject[formFieldName] !== currentFieldValue;
      }
    });

    if (isMismatched) {
      this.applyMultiValue(formField, formFieldName, finalAssetObject);
    }
  }

  private applyMultiValue(
    formField: FormControlModel,
    formFieldName: string,
    finalAssetObject: AssetUIModel,
    isCustomField = false,
    selectedAssets?: AssetUIModel[],
  ) {
    const targetObject = isCustomField ? (finalAssetObject.customFields = finalAssetObject.customFields || {}) : finalAssetObject;

    switch (formField.fieldType) {
      case 'dropdown': {
        this.ensureMultiValueInOptions(formField);
        targetObject[formFieldName] = isCustomField ? MULTI_VALUE : { _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE };
        break;
      }

      case 'multiSelectChip': {
        if (isCustomField && selectedAssets) {
          this.ensureMultiValueInOptions(formField, true);
          const multiDimensionalArray: string[][] = selectedAssets.map((asset) => asset.customFields?.[formFieldName]).filter(Array.isArray);
          const commonValues = findCommonValues(multiDimensionalArray);
          targetObject[formFieldName] = [...commonValues, MULTI_VALUE];
        }
        break;
      }

      case 'number':
      case 'date': {
        targetObject[formFieldName] = '';
        formField.placeholder = PROMPTS.TEXT.MULTI_VALUE;
        break;
      }

      case AssetMetadata.General.DOMAINS: {
        if (!isCustomField) {
          targetObject[formFieldName] = {
            agency: MULTI_VALUE,
            client: MULTI_VALUE,
            brand: MULTI_VALUE,
          };
        }
        break;
      }

      default: {
        targetObject[formFieldName] = MULTI_VALUE;
        break;
      }
    }
  }

  private processProductionField(formFields: FormControlModel[], finalAssetObject: AssetUIModel) {
    finalAssetObject.customFields = finalAssetObject.customFields || {};
    finalAssetObject.customFields['productionDetails'] = this.data.productionDetails || {};

    for (const formField of formFields) {
      if (formField.fieldType === 'dropdown') {
        this.ensureMultiValueInOptions(formField);
      }
      if (formField.fieldType === 'formArray' && formField.formFieldGroup) {
        for (const formArrayFormField of formField.formFieldGroup) {
          if (
            formArrayFormField.fieldType === 'date' &&
            finalAssetObject.customFields['productionDetails'][formField.formFieldName]?.[0][formArrayFormField.formFieldName] === MULTI_VALUE
          ) {
            formArrayFormField.placeholder = PROMPTS.TEXT.MULTI_VALUE;
          }
        }
      }
    }
  }

  private processCustomField(formField: FormControlModel, formFieldName: string, selectedAssets: AssetUIModel[], finalAssetObject: AssetUIModel) {
    finalAssetObject.customFields = finalAssetObject.customFields || {};

    const currentFieldValue = finalAssetObject.customFields[formFieldName];

    let isMismatched = false;

    if (formField.fieldType === 'multiSelectChip') {
      isMismatched = selectedAssets.some((asset) => !arraysEqual(currentFieldValue || [], asset.customFields?.[formFieldName] || []));
    } else if (formField.fieldType === 'formArray') {
      // Do nothing
    } else {
      isMismatched = selectedAssets.some((asset) => {
        if (Array.isArray(asset.customFields?.[formFieldName]) && Array.isArray(currentFieldValue)) {
          return !arraysEqual(currentFieldValue || [], asset.customFields?.[formFieldName] || []);
        }
        return asset.customFields?.[formFieldName] !== currentFieldValue;
      });
    }

    if (isMismatched) {
      this.applyMultiValue(formField, formFieldName, finalAssetObject, true, selectedAssets);
    }
  }

  private ensureMultiValueInOptions(formField: FormControlModel, isMultiSelectChip = false) {
    const exists = formField.options?.some((option) => option._id === MULTI_VALUE);
    if (!exists && this.existingAssetListFiles.length > 1) {
      if (isMultiSelectChip && formField.groupListView && formField.options && formField.options.length > 0) {
        const lastIndex = formField.options.length - 1;
        formField.options[lastIndex].lists.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
      } else {
        formField.options?.push({ _id: MULTI_VALUE, name: PROMPTS.TEXT.MULTI_VALUE });
      }
    }
  }

  async onSubmit(event: AnyType) {
    this.updateAssetData(event);
  }

  async transformRelationData(updatedRelationData: AnyType) {
    const relationUpsertData: AnyType = [];
    if (this.storedRelationData.assets && this.storedRelationData.assets.length > 0) {
      this.storedRelationData.assets.forEach((relationAsset: AnyType) => {
        if (relationAsset.relationTypes && relationAsset.relationTypes.length > 0) {
          relationAsset.relationTypes.forEach((relationType: AnyType) => {
            const relationObject = {
              fromEntityId: relationAsset.assetId,
              fromEntity: 'Asset',
              toEntityId: this.selectedAssetIds[0],
              toEntity: 'Asset',
              deleted: true,
              relationshipType: relationType._id as unknown as string,
            };
            relationUpsertData.push(relationObject);
          });
        }
      });
    }
    if (updatedRelationData.assets && updatedRelationData.assets.length > 0) {
      updatedRelationData.assets.forEach((relatedAsset: AnyType) => {
        if (relatedAsset.relationTypes && relatedAsset.relationTypes.length > 0) {
          relatedAsset.relationTypes.forEach((relationType1: AnyType) => {
            const idx = relationUpsertData.findIndex((relationObject: AnyType) => {
              return relationObject.fromEntityId == relatedAsset.assetId && relationObject.relationshipType == relationType1._id;
            });
            if (idx > -1) {
              relationUpsertData.splice(idx, 1);
            } else {
              const relationObject = {
                fromEntityId: relatedAsset.assetId,
                fromEntity: 'Asset',
                toEntityId: this.selectedAssetIds[0],
                toEntity: 'Asset',
                relationshipType: relationType1._id as unknown as string,
              };
              relationUpsertData.push(relationObject);
            }
          });
        }
      });
    }
    let relationFinalData: AnyType = [];
    if (this.selectedAssetIds.length > 0) {
      relationUpsertData.forEach((relationData: AnyType) => {
        this.selectedAssetIds.forEach((assetId: string) => {
          const relationObject = JSON.parse(JSON.stringify(relationData));
          relationObject.toEntityId = assetId;
          relationFinalData.push(relationObject);
        });
      });
    } else {
      relationFinalData = relationUpsertData;
    }
    this.dialogRef.close();
    if (relationFinalData.length > 0) {
      const successResult = await Promise.all(
        relationFinalData.map(async (relationData: AnyType) => {
          if (relationData.deleted) {
            const request: CreateAssetRelationDto = relationData;
            this.relationService.relationsControllerDeleteRelation(request).subscribe({
              error: this.clientNotificationHandlerService.errorNotification,
            });
          } else {
            const request: CreateAssetRelationDto = relationData;
            this.relationService.relationsControllerCreateRelation(request).subscribe({
              error: this.clientNotificationHandlerService.errorNotification,
            });
          }
          return true;
        }),
      );
      if (successResult) {
        if (this.data.assets.length && this.brandingKey === CustomClients.Vorwerk) {
          this.data.assets.forEach((asset: AssetUIModel) => {
            this.assetsService.assetsControllerUpdateStatus(asset._id, Asset.StatusEnum.AwaitingApproval).subscribe();
            asset.status = Asset.StatusEnum.AwaitingApproval;
          });
          const responseData = this.assetUrlService.generatePreview(this.data.assets);
          this.assetCommonService.assetDataSubject$.next(responseData[0]);
        }
        this.dialogRef.close('success');
        this.clientNotificationHandlerService.successNotification($localize`Assets updated successfully`);
      }
    }
  }

  async updateAssetData(updateForm: AnyType) {
    if (this.isFormEmpty(updateForm)) return;

    if (this.stepperType === 'relations') {
      this.handleRelations(updateForm);
      return;
    }

    let updateData: AnyType = {};
    let customFormValues: AnyType = {};

    updateData = handleGeneralInformation(updateForm, updateData, this.brandingKey);

    await this.processTags(updateForm, updateData);

    if (updateForm.tags || updateForm.generalInformation || updateForm.productionDetails) {
      if (
        updateForm.generalInformation &&
        Object.prototype.hasOwnProperty.call(updateForm.generalInformation, 'plannedDeletionToggle') &&
        !updateForm.generalInformation.plannedDeletionToggle
      ) {
        updateData.plannedDeletion = '';
        updateData.plannedDeletionUser = '';
      }

      switch (this.brandingKey) {
        case CustomClients.Vorwerk:
          customFormValues = handleVorwerkBranding(updateForm, this.assetCommonService);
          this.handleDomains(customFormValues, updateData);
          this.cleanCustomFields(customFormValues, updateData);
          updateData = { ...updateData, customFields: customFormValues };
          if (this.commonUtilityLib.isEmptyObject(updateData['customFields'])) delete updateData['customFields'];
          updateData.status = getStatusOrAwaitingApproval(updateData);
          break;

        case CustomClients.VKB:
          customFormValues = this.handleVKBBranding(updateForm, 'metaData');
          this.cleanCustomFields(customFormValues, updateData);
          updateData = { ...updateData, ...updateForm.generalInformation, customFields: customFormValues };
          break;

        case CustomClients.SEAT:
          customFormValues = this.handleSeatBranding(updateForm, 'metaData');
          this.handleDomains(customFormValues, updateForm.generalInformation);
          this.cleanCustomFields(customFormValues, updateData);
          updateData = { ...updateData, ...updateForm.generalInformation, customFields: customFormValues };
          break;

        case CustomClients.LufthansaMediacenter:
        case CustomClients.LufthansaMedialounge:
          customFormValues = this.handleLufthansaBranding(updateForm);
          this.cleanCustomFields(customFormValues, updateData);
          updateData = { ...updateData, ...updateForm.generalInformation, customFields: customFormValues };
          break;

        case CustomClients.Standard:
        default:
          customFormValues = handleStandardBranding(updateForm, this.assetCommonService);
          this.cleanCustomFields(customFormValues, updateData);
          updateData = { ...updateData, ...updateForm.generalInformation, customFields: customFormValues };
          break;
      }
    }

    if (updateForm.usageRight) {
      updateForm.usageRight.owner = updateForm.usageRight.owner && updateForm.usageRight.owner.id;

      if (this.brandingKey === CustomClients.Arval) {
        const usageRightKeysToExclude = ['attachments', 'expiryDate', 'noExpiry', 'notes', 'owner'];
        customFormValues = {
          ...this.assetCommonService.transformAndFilterKeys(updateForm.usageRight, usageRightKeysToExclude),
        };
        updateData = { ...updateData, ...updateForm.usageRight, customFields: customFormValues };
      } else if (this.brandingKey === CustomClients.VKB || this.brandingKey === CustomClients.SEAT) {
        if (this.brandingKey === CustomClients.VKB) {
          customFormValues = this.handleVKBBranding(updateForm, AssetMetadata.Rights.USAGE_RIGHTS);
          if (updateForm.usageRight?.noExpiry) updateData.expiryDate = null;
          if (updateForm.usageRight?.expiryDate && updateForm.usageRight?.expiryDate < new Date()) {
            updateData.status = Asset.StatusEnum.Expired;
          }
        } else if (this.brandingKey === CustomClients.SEAT) {
          customFormValues = this.handleSeatBranding(updateForm, AssetMetadata.Rights.USAGE_RIGHTS);
        }
        this.cleanCustomFields(customFormValues, updateData);
        updateData = { ...updateData, ...updateForm.usageRight, customFields: customFormValues };
      } else {
        updateData = { ...updateData, ...updateForm.usageRight };
      }
    }

    if (this.selectedAssetIds.length > 1) {
      this.cleanMultiValueFields(updateData);
      if (this.brandingKey === CustomClients.SEAT && this.hasMultiValueInDomains(updateData)) {
        delete updateData[AssetMetadata.General.DOMAINS];
      }
    }

    const { customFields = {}, ...restOfUpdateData } = updateData;
    let productionDetails: UpdateProductionDetailsDto['productionDetails'] = {};
    let customFieldsWithoutProductionDetails = { ...customFields };
    // Only process productionDetails if brandingKey is CustomClients.Standard
    if (this.brandingKey === CustomClients.Standard) {
      ({ productionDetails, ...customFieldsWithoutProductionDetails } = customFields);
    }

    updateData = {
      ...restOfUpdateData,
      customFields: customFieldsWithoutProductionDetails,
    };

    const formValues: UpdateAssetManyDto = {
      ids: this.selectedAssetIds,
      updateData,
    };

    this.assetsService
      .assetsControllerUpdateMany(formValues)
      .pipe(
        switchMap((assetsResponse) =>
          // Only update productionDetails if brandingKey is Standard and productionDetails exists
          iif(
            () => this.brandingKey === CustomClients.Standard && productionDetails && Object.keys(productionDetails).length > 0,
            (() => {
              const productionDetailsFormValues: UpdateProductionDetailsDto = {
                ids: this.selectedAssetIds,
                productionDetails,
              };
              return this.bulkEditApiService.assetsBulkEditControllerUpdateProductionDetails(productionDetailsFormValues).pipe(
                map((productionDetailsResponse) => ({
                  assetsResponse,
                  productionDetailsResponse,
                })),
              );
            })(),
            // If falsy, it skips the API and just returns productionDetailsResponse as null.
            of({ assetsResponse, productionDetailsResponse: null }),
          ),
        ),
      )
      .subscribe({
        next: ({ assetsResponse, productionDetailsResponse }) => {
          this.dialogRef.close(assetsResponse);
          this.clientNotificationHandlerService.successNotification($localize`Assets updated successfully`);
          if (productionDetailsResponse) this.clientNotificationHandlerService.successNotification($localize`Production Details Updated`);
        },
        error: (err) => this.clientNotificationHandlerService.errorNotification(err),
      });
  }

  private hasMultiValueInDomains(data: AnyType): boolean {
    if (!data?.domains || !Array.isArray(data.domains)) {
      return false;
    }

    return data.domains.some((domain: AnyType) => Object.values(domain).some((value) => value === MULTI_VALUE || value === `<p>${MULTI_VALUE}</p>`));
  }

  private async processTags(updateForm: AnyType, updateData: AnyType) {
    if (!updateForm.tags) return;

    if (updateForm.tags.tags) {
      updateForm.tags.tags = await this.assetCommonService.modifiedTags(updateForm.tags.tags);

      if (this.selectedAssetIds.length > 1) {
        updateData['removedTags'] = this.updateRemovedTags(updateForm.tags.tags);
      }

      updateData[AssetMetadata.General.TAGS] = updateForm.tags.tags;
    }

    if (this.brandingKey === CustomClients.Arval) {
      const tagsKeysToExclude = [AssetMetadata.General.TAGS];
      const customFields = this.assetCommonService.transformAndFilterKeys(updateForm.tags, tagsKeysToExclude);
      updateData.customFields = { ...customFields };
    }
  }

  private handleDomains(customFormValues: AnyType, target: AnyType) {
    if (customFormValues[AssetMetadata.General.DOMAINS]) {
      target.domains = customFormValues[AssetMetadata.General.DOMAINS];
      delete customFormValues[AssetMetadata.General.DOMAINS];
    }
  }

  private cleanCustomFields(customFormValues: AnyType, updateData: AnyType) {
    for (const key in customFormValues) {
      let keyValue = customFormValues[key];
      if (typeof keyValue === 'string') keyValue = keyValue.replace(' ', '');
      if (keyValue == MULTI_VALUE || keyValue == '<p>' + MULTI_VALUE + '</p>') {
        delete customFormValues[key];
        delete updateData[key];
      }
    }
  }

  private cleanMultiValueFields(updateData: AnyType) {
    for (const key in updateData) {
      let keyValue = updateData[key] && updateData[key]._id ? updateData[key]._id : updateData[key];
      if (typeof keyValue === 'string') keyValue = keyValue.replace(' ', '');
      if (keyValue == MULTI_VALUE || key == 'name' || keyValue == '<p>' + MULTI_VALUE + '</p>') {
        delete updateData[key];
      }
    }
  }

  private isFormEmpty(updateForm: AnyType): boolean {
    if (this.commonUtilityLib.isEmptyObject(updateForm)) {
      this.dialogRef.close();
      return true;
    }
    return false;
  }

  private handleRelations(updateForm: AnyType) {
    if (updateForm.defineAssetRelation && updateForm.defineAssetRelation.assetRelation) {
      this.transformRelationData(updateForm.defineAssetRelation.assetRelation);
    } else {
      this.dialogRef.close('success');
      this.clientNotificationHandlerService.successNotification($localize`Assets updated successfully`);
    }
  }

  updateRemovedTags(commonTags: string[]) {
    let removalObject: string[] = [];
    this.selectedAssetIds.forEach((element: string) => {
      const currentAsset = this.existingAssetListFiles.find((asset: AnyType) => element == asset._id);
      if (currentAsset) {
        let removedTags: AnyType = currentAsset.tags;
        if (removedTags && removedTags.length > 0) {
          removedTags = removedTags.filter(function (obj: string) {
            return commonTags.indexOf(obj) == -1;
          });
          removedTags = removedTags.filter((obj: string) => {
            return this.cumulatedTags.findIndex((tags: AnyType) => tags._id == obj) == -1;
          });

          removalObject = [...removalObject, ...removedTags];
        }
      }
    });
    return removalObject;
  }

  /**
   * Handle SEAT customer asset metadata
   * @param rawFormValues
   * @returns
   */
  handleSeatBranding(rawFormValues: AnyType, stepperType: string) {
    if (stepperType === 'metaData') {
      const tagsKeysToExclude: string[] = [AssetMetadata.General.TAGS];
      const generalInformationKeysToExclude = ['agency', 'client', 'brand', 'status', 'name'];
      if (rawFormValues.generalInformation.domains) {
        rawFormValues.generalInformation.domains = this.assetCommonService.transformVorwerkDomains(rawFormValues.generalInformation.domains);
      }
      return {
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.tags, tagsKeysToExclude),
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.contentDescription, []),
      };
    } else {
      const usageRightKeysToExclude: string[] = ['expiryDate'];
      return {
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.usageRight, usageRightKeysToExclude),
      };
    }
  }

  /**
   * Handle VKB customer asset metadata
   * @param rawFormValues
   * @returns
   */
  handleVKBBranding(rawFormValues: AnyType, stepperType: string) {
    if (stepperType === 'metaData') {
      const tagsKeysToExclude: string[] = [AssetMetadata.General.TAGS];
      const generalInformationKeysToExclude = ['agency', 'client', 'brand', 'status', 'name', 'plannedDeletionToggle', 'plannedDeletion'];
      return {
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.tags, tagsKeysToExclude),
      };
    } else {
      const usageRightKeysToExclude: string[] = ['expiryDate', 'owner', 'noExpiry'];
      return {
        ...this.assetCommonService.transformAndFilterKeys(rawFormValues.usageRight, usageRightKeysToExclude),
      };
    }
  }

  handleLufthansaBranding(rawFormValues: AnyType) {
    const tagsKeysToExclude = [AssetMetadata.General.TAGS];
    const generalInformationKeysToExclude = ['agency', 'client', 'brand', 'status', 'name', 'description', 'publishStartDate', 'publishEndDate'];
    return {
      ...this.assetCommonService.transformAndFilterKeys(rawFormValues.tags, tagsKeysToExclude),
      ...this.assetCommonService.transformAndFilterKeys(rawFormValues.generalInformation, generalInformationKeysToExclude),
    };
  }

  private populateUsers(): Promise<UserInfo[]> {
    return new Promise((resolve) => {
      if (this.stepperType === AssetMetadata.Rights.USAGE_RIGHTS) {
        this.valueListsService.listUsers().subscribe((users) => {
          this.users = users;
          resolve(this.users);
        });
      } else {
        resolve(this.users);
      }
    });
  }
}
